import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type AnchorHTMLAttributes,
  type ReactNode,
} from 'react';

interface RouterContextType {
  location: string;
  scrollTarget: string | null;
  setLocation: (path: string, scrollTarget?: string) => void;
  clearScrollTarget: () => void;
}

type LocationParts = Pick<Location, 'pathname' | 'search' | 'hash'>;

export const LEGACY_HASH_ROUTES: Readonly<Record<string, string>> = {
  '#villas': '/villas',
  '#experience': '/amenities-experience',
  '#events': '/events-weddings',
  '#info': '/guest-info',
  '#contact': '/contact',
};

/**
 * Returns the modern URL for a legacy homepage section URL. Search parameters
 * deliberately remain in front of the old hash and are carried to the new page.
 */
export function getLegacyHashRedirect(location: LocationParts): string | null {
  if (location.pathname !== '/' && location.pathname !== '') return null;
  const route = LEGACY_HASH_ROUTES[location.hash.toLowerCase()];
  return route ? `${route}${location.search}` : null;
}

/** Redirects the retired listings URL to the canonical Villas page. */
export function getLegacyPathRedirect(location: LocationParts): string | null {
  return location.pathname === '/listings'
    ? `/villas${location.search}${location.hash}`
    : null;
}

function getLegacyRedirect(location: LocationParts): string | null {
  return getLegacyPathRedirect(location) ?? getLegacyHashRedirect(location);
}

export function getRoutePath(location: string): string {
  try {
    return new URL(location, 'https://tokaridge.co.nz').pathname || '/';
  } catch {
    return location.split(/[?#]/, 1)[0] || '/';
  }
}

export function resolveInternalDestination(destination: string, baseHref: string): string {
  const url = new URL(destination, baseHref);
  return getLegacyRedirect(url) ?? `${url.pathname || '/'}${url.search}${url.hash}`;
}

export function shouldScrollToRouteTop(scrollTarget: string | null): boolean {
  return scrollTarget === null || scrollTarget === 'top';
}

const RouterContext = createContext<RouterContextType | null>(null);

function currentLocation(): string {
  return `${window.location.pathname || '/'}${window.location.search}`;
}

function redirectLegacyRoute(replace: boolean): string | null {
  const redirect = getLegacyRedirect(window.location);
  if (!redirect) return null;
  window.history[replace ? 'replaceState' : 'pushState']({}, '', redirect);
  return redirect;
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocationState] = useState(() => {
    return redirectLegacyRoute(true) ?? currentLocation();
  });
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);

  const setLocation = useCallback((destination: string, target?: string) => {
    const next = resolveInternalDestination(destination, window.location.href);

    const previousPage = getRoutePath(next) === window.location.pathname
      ? window.history.state?.previousPage
      : `${currentLocation()}${window.location.hash}`;
    window.history.pushState({ previousPage }, '', next);
    setLocationState(`${window.location.pathname || '/'}${window.location.search}`);
    setScrollTarget(target ?? null);
    window.dispatchEvent(new CustomEvent('router-navigation', {
      detail: {
        path: `${window.location.pathname || '/'}${window.location.search}`,
        scrollTarget: target,
      },
    }));
  }, []);

  const clearScrollTarget = useCallback(() => setScrollTarget(null), []);

  useEffect(() => {
    const syncFromBrowser = () => {
      const redirect = redirectLegacyRoute(true);
      setLocationState(redirect ?? currentLocation());
      setScrollTarget(null);
    };

    const syncFromRouterEvent = (event: Event) => {
      const detail = (event as CustomEvent<{ scrollTarget?: string }>).detail;
      const redirect = redirectLegacyRoute(true);
      setLocationState(redirect ?? currentLocation());
      setScrollTarget(detail?.scrollTarget ?? null);
    };

    window.addEventListener('popstate', syncFromBrowser);
    window.addEventListener('hashchange', syncFromBrowser);
    window.addEventListener('router-navigation', syncFromRouterEvent);
    return () => {
      window.removeEventListener('popstate', syncFromBrowser);
      window.removeEventListener('hashchange', syncFromBrowser);
      window.removeEventListener('router-navigation', syncFromRouterEvent);
    };
  }, []);

  return (
    <RouterContext.Provider value={{ location, scrollTarget, setLocation, clearScrollTarget }}>
      {children}
    </RouterContext.Provider>
  );
}

export function useLocation(): [string, (path: string, scrollTarget?: string) => void] {
  const context = useContext(RouterContext);
  if (!context) throw new Error('useLocation must be used within RouterProvider');
  return [context.location, context.setLocation];
}

export function useScrollTarget(): [string | null, () => void] {
  const context = useContext(RouterContext);
  if (!context) throw new Error('useScrollTarget must be used within RouterProvider');
  return [context.scrollTarget, context.clearScrollTarget];
}

export function useParams(): Record<string, string> {
  const [location] = useLocation();
  const pathname = getRoutePath(location);
  const parts = pathname.split('/');

  if (pathname.startsWith('/blog/') && parts[2]) return { id: decodeURIComponent(parts[2]) };
  if (pathname.startsWith('/property/') && parts[2]) return { id: decodeURIComponent(parts[2]) };
  if (pathname.startsWith('/booking-confirmation/') && parts[2]) {
    return { bookingId: decodeURIComponent(parts[2]) };
  }
  return {};
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

export function Link({ href, children, className, onClick, ...props }: LinkProps) {
  const context = useContext(RouterContext);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      props.target === '_blank'
    ) return;

    if (!context) return;
    event.preventDefault();

    if (href.startsWith('#') && !LEGACY_HASH_ROUTES[href.toLowerCase()]) {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    context.setLocation(href);
  };

  return (
    <a href={href} onClick={handleClick} className={className} {...props}>
      {children}
    </a>
  );
}